import { getAuthHeaders } from './client';
import client from './client';
import { API_BASE_URL } from '@/utils/constants';
import type { SSECallback } from '@/utils/sseParser';
import { parseSSEStream } from '@/utils/sseParser';
import type { SuggestionsResponse } from '@/types/ares';
import type { ClarificationChoice } from '@/types/chat';
import type { GeoJSONFeature } from '@/types/map';
import type { DashboardStats } from '@/types/dashboard';

export interface QueryStreamOptions {
  clarificationChoice?: ClarificationChoice;
}

export async function queryStream(
  query: string,
  sessionId: string,
  onEvent: SSECallback,
  onError: (error: Error) => void,
  signal?: AbortSignal,
  options?: QueryStreamOptions
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/ares/query/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      query,
      session_id: sessionId,
      ...(options?.clarificationChoice
        ? { clarification_choice: options.clarificationChoice }
        : {}),
    }),
    signal,
  });

  if (response.status === 401) {
    localStorage.clear();
    window.location.href = '/login';
    return;
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  await parseSSEStream(response, onEvent, onError, signal);
}

export async function fetchSuggestions(): Promise<SuggestionsResponse> {
  const { data } = await client.get<SuggestionsResponse>('/ares/suggestions');
  return data;
}

export async function clearContext(sessionId?: string): Promise<void> {
  await client.post('/ares/clear-context', sessionId ? { session_id: sessionId } : {});
}

export async function getStatus(): Promise<{ status: string }> {
  const { data } = await client.get('/ares/status');
  return data;
}

export async function getHighwaysGeoJSON(): Promise<unknown> {
  const { data } = await client.get('/ares/highways/geojson');
  return data;
}

export async function notifyTransportLine(tripData: Record<string, unknown>): Promise<void> {
  await client.post('/ares/fleet/notify-transport-line', tripData);
}

export interface InitialMapResponse {
  success: boolean;
  message: string;
  intent?: string;
  map?: { features: unknown[] };
  map_data?: { features: unknown[] };
  stats?: Record<string, unknown>;
}

export function triggerExcelDownload(exportId: string): void {
  const headers = getAuthHeaders();
  const url = `${API_BASE_URL}/ares/export/${exportId}`;

  fetch(url, { headers })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Extract filename from Content-Disposition if available
      const disposition = res.headers.get('Content-Disposition');
      const filenameMatch = disposition?.match(/filename="?(.+?)"?$/);
      const filename = filenameMatch?.[1] || `ARES_export.xlsx`;
      return res.blob().then((blob) => ({ blob, filename }));
    })
    .then(({ blob, filename }) => {
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(blobUrl);
      a.remove();
    })
    .catch((err) => {
      console.error('Excel download failed:', err);
    });
}

export async function queryInitialMap(query: string = 'mostrar mapa'): Promise<InitialMapResponse> {
  const { data } = await client.post<InitialMapResponse>('/ares/query', {
    query,
    save_history: false,
  });
  return data;
}

export interface FleetAllResponse {
  success: boolean;
  count: number;
  stats?: DashboardStats;
  map: {
    type: "FeatureCollection";
    features: GeoJSONFeature[];
  };
  error?: string;
}

export async function fetchAllFleet(): Promise<FleetAllResponse> {
  const { data } = await client.get<FleetAllResponse>('/fleet/all');
  return data;
}

// ============ Session Management ============

export interface NewSessionResponse {
  success: boolean;
  message: string;
  user_id: string;
  session_id: string;
  keep_learned_data: boolean;
  cleared_keys_count: number;
  timestamp: string;
}

/**
 * Inicia una nueva sesión de conversación limpia.
 * 
 * Llamar cuando la app se carga (después de autenticación) para:
 * - Limpiar el historial de conversación anterior
 * - Prevenir que filtros de sesiones previas contaminen la nueva sesión
 * - Mantener los datos aprendidos del usuario (sinónimos, patrones)
 */
export async function startNewSession(keepLearnedData: boolean = true): Promise<NewSessionResponse> {
  const { data } = await client.post<NewSessionResponse>('/api/learning/session/new', {
    keep_learned_data: keepLearnedData
  });
  return data;
}
